/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        RAG Knowledge Base & Document Ingestion Data Transfer Objects (OpenAPI 3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

// =============================================================================
// ENUMS & CONSTANTS
// =============================================================================

export const KnowledgeSourceTypeEnum = z
  .enum(['pdf', 'docx', 'txt', 'md', 'url'])
  .openapi({
    description: 'Format or origin type of the knowledge ingestion source',
    example: 'pdf',
  });

export type KnowledgeSourceType = z.infer<typeof KnowledgeSourceTypeEnum>;

export const KnowledgeSourceStatusEnum = z
  .enum(['pending', 'processing', 'ready', 'failed'])
  .openapi({
    description: 'Current lifecycle processing status of the knowledge document',
    example: 'ready',
  });

export type KnowledgeSourceStatus = z.infer<typeof KnowledgeSourceStatusEnum>;

// =============================================================================
// 1. KNOWLEDGE BASE CRUD SCHEMAS
// =============================================================================

export const CreateKnowledgeBaseSchema = z
  .object({
    name: z
      .string()
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must not exceed 100 characters')
      .openapi({
        description: 'Human-readable title for the knowledge base / custom bot',
        example: 'DevOps & Cloudflare Technical Knowledge',
      }),
    description: z
      .string()
      .max(500, 'Description must not exceed 500 characters')
      .optional()
      .openapi({
        description: 'Brief overview of this knowledge repository scope',
        example: 'Internal manuals, architectural guides and API specifications.',
      }),
    systemPrompt: z
      .string()
      .optional()
      .default(
        'You are an intelligent technical knowledge assistant. Answer questions based on the retrieved context accurately and concisely.'
      )
      .openapi({
        description: 'Core instructions guiding AI persona when answering RAG queries',
        example: 'You are an enterprise technical support specialist. Always cite referenced sources.',
      }),
    welcomeMessage: z
      .string()
      .optional()
      .default('Hello! How can I assist you with this knowledge base today?')
      .openapi({
        description: 'Introductory greeting shown when the chat widget opens',
        example: 'Hello! Ask me any question regarding our cloud architecture.',
      }),
    allowedDomains: z
      .array(z.string())
      .optional()
      .default(['*'])
      .openapi({
        description: 'Domain origins permitted to embed the standalone chatbot widget (CORS protection)',
        example: ['https://example.com', 'https://docs.example.com'],
      }),
    widgetTheme: z
      .record(z.unknown())
      .optional()
      .default({})
      .openapi({
        description: 'Custom UI styling configuration for the chatbot widget (colors, logo, placement)',
        example: { primaryColor: '#2563eb', position: 'bottom-right' },
      }),
  })
  .openapi('CreateKnowledgeBaseRequest');

export type CreateKnowledgeBaseRequest = z.infer<typeof CreateKnowledgeBaseSchema>;

export const UpdateKnowledgeBaseSchema = z
  .object({
    name: z.string().min(2).max(100).optional(),
    description: z.string().max(500).optional(),
    systemPrompt: z.string().optional(),
    welcomeMessage: z.string().optional(),
    allowedDomains: z.array(z.string()).optional(),
    widgetTheme: z.record(z.unknown()).optional(),
  })
  .openapi('UpdateKnowledgeBaseRequest');

export type UpdateKnowledgeBaseRequest = z.infer<typeof UpdateKnowledgeBaseSchema>;

export const KnowledgeBaseSummarySchema = z
  .object({
    id: z.string().uuid().openapi({ example: '123e4567-e89b-12d3-a456-426614174000' }),
    workspaceId: z.string().uuid().openapi({ example: '123e4567-e89b-12d3-a456-426614174001' }),
    name: z.string().openapi({ example: 'Cloud Architecture Hub' }),
    description: z.string().nullable().optional(),
    systemPrompt: z.string().openapi({ example: 'You are an enterprise architect assistant.' }),
    welcomeMessage: z.string().nullable().optional(),
    allowedDomains: z.array(z.string()).openapi({ example: ['*'] }),
    widgetTheme: z.record(z.unknown()),
    vectorNamespace: z.string().openapi({ example: 'kb_a1b2c3d4e5f6' }),
    totalDocuments: z.number().int().openapi({ example: 8 }),
    totalChunks: z.number().int().openapi({ example: 142 }),
    createdAt: z.string().openapi({ example: '2026-10-20T07:00:00.000Z' }),
  })
  .openapi('KnowledgeBaseSummary');

export type KnowledgeBaseSummary = z.infer<typeof KnowledgeBaseSummarySchema>;

export const KnowledgeBaseQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1).openapi({ example: 1 }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({ example: 20 }),
    search: z.string().optional().openapi({ example: 'cloud' }),
  })
  .openapi('KnowledgeBaseQuery');

export type KnowledgeBaseQuery = z.infer<typeof KnowledgeBaseQuerySchema>;

// =============================================================================
// 2. DOCUMENT INGESTION & UPLOAD SCHEMAS
// =============================================================================

export const PresignedUploadRequestSchema = z
  .object({
    fileName: z
      .string()
      .min(1, 'File name is required')
      .max(255, 'File name too long')
      .openapi({ example: 'cloudflare_v8_architecture.pdf' }),
    fileType: z
      .enum(['pdf', 'docx', 'txt', 'md'])
      .openapi({ description: 'File extension/type to ingest', example: 'pdf' }),
    fileSize: z
      .number()
      .int()
      .positive()
      .max(52428800, 'File size exceeds maximum allowed threshold of 50MB')
      .openapi({ description: 'Size in bytes', example: 2048576 }),
  })
  .openapi('PresignedUploadRequest');

export type PresignedUploadRequest = z.infer<typeof PresignedUploadRequestSchema>;

export const PresignedUploadResponseSchema = z
  .object({
    documentId: z.string().uuid().openapi({ example: '123e4567-e89b-12d3-a456-426614174000' }),
    uploadUrl: z
      .string()
      .url()
      .openapi({ example: 'https://r2.sophiaplatform.com/workspaces/ws-1/knowledge/doc-1?X-Amz-Signature=...' }),
    storageKey: z
      .string()
      .openapi({ example: 'workspaces/ws-1/knowledge/doc-1_cloudflare_v8_architecture.pdf' }),
    expiresIn: z.number().int().openapi({ description: 'Expiration in seconds', example: 3600 }),
  })
  .openapi('PresignedUploadResponse');

export type PresignedUploadResponse = z.infer<typeof PresignedUploadResponseSchema>;

export const IngestUrlRequestSchema = z
  .object({
    url: z.string().url('A valid HTTP/HTTPS URL is required').openapi({
      description: 'Web page URL to scrape, clean, and convert into markdown knowledge',
      example: 'https://blog.cloudflare.com/workers-ai-streaming',
    }),
    name: z.string().max(255).optional().openapi({
      description: 'Custom document title. Defaults to web page title or URL',
      example: 'Cloudflare Workers AI Streaming Guide',
    }),
  })
  .openapi('IngestUrlRequest');

export type IngestUrlRequest = z.infer<typeof IngestUrlRequestSchema>;

export const ProcessDocumentRequestSchema = z
  .object({
    chunkSize: z
      .number()
      .int()
      .min(100)
      .max(2000)
      .default(500)
      .openapi({ description: 'Target word count per chunk', example: 500 }),
    chunkOverlap: z
      .number()
      .int()
      .min(0)
      .max(200)
      .default(50)
      .openapi({ description: 'Word overlap between consecutive chunks', example: 50 }),
  })
  .openapi('ProcessDocumentRequest');

export type ProcessDocumentRequest = z.infer<typeof ProcessDocumentRequestSchema>;

export const DocumentChunkSchema = z
  .object({
    chunkIndex: z.number().int().openapi({ example: 0 }),
    text: z.string().openapi({ example: 'Cloudflare Workers run on V8 isolates...' }),
    wordCount: z.number().int().openapi({ example: 480 }),
    pageNumber: z.number().int().optional().openapi({ example: 1 }),
  })
  .openapi('DocumentChunk');

export type DocumentChunk = z.infer<typeof DocumentChunkSchema>;

export const KnowledgeDocumentResponseSchema = z
  .object({
    id: z.string().uuid().openapi({ example: '123e4567-e89b-12d3-a456-426614174000' }),
    botId: z.string().uuid().openapi({ example: '123e4567-e89b-12d3-a456-426614174001' }),
    sourceType: KnowledgeSourceTypeEnum,
    name: z.string().openapi({ example: 'System Architecture Whitepaper.pdf' }),
    fileR2Url: z.string().nullable().optional(),
    totalChunks: z.number().int().openapi({ example: 24 }),
    status: KnowledgeSourceStatusEnum,
    createdAt: z.string().openapi({ example: '2026-10-20T07:00:00.000Z' }),
  })
  .openapi('KnowledgeDocumentResponse');

export type KnowledgeDocumentResponse = z.infer<typeof KnowledgeDocumentResponseSchema>;

export const ProcessDocumentResponseSchema = z
  .object({
    documentId: z.string().uuid(),
    status: KnowledgeSourceStatusEnum,
    totalChunks: z.number().int(),
    totalWords: z.number().int(),
    previewChunks: z.array(DocumentChunkSchema).max(3),
  })
  .openapi('ProcessDocumentResponse');

export type ProcessDocumentResponse = z.infer<typeof ProcessDocumentResponseSchema>;

// =============================================================================
// 4. RETRIEVAL & CITATIONS SCHEMAS (Task 5.2.3 & 5.2.4)
// =============================================================================

export const RagCitationSchema = z
  .object({
    documentId: z.string().uuid().openapi({ example: '123e4567-e89b-12d3-a456-426614174000' }),
    fileName: z.string().openapi({ example: 'Architecture_Guide.pdf' }),
    pageNumber: z.number().int().optional().openapi({ example: 4 }),
    chunkIndex: z.number().int().openapi({ example: 2 }),
    snippet: z.string().openapi({ example: 'Cloudflare Workers run on V8 isolates with zero cold-start...' }),
    score: z.number().min(0).max(1).openapi({ example: 0.88 }),
  })
  .openapi('RagCitation');

export type RagCitation = z.infer<typeof RagCitationSchema>;

export const QueryKnowledgeBaseRequestSchema = z
  .object({
    query: z
      .string()
      .min(2, 'Query must be at least 2 characters')
      .max(1000, 'Query must not exceed 1000 characters')
      .openapi({
        description: 'Question or contextual prompt used for vector similarity lookup',
        example: 'How does Cloudflare Vectorize achieve low latency querying?',
      }),
    topK: z
      .number()
      .int()
      .min(1)
      .max(20)
      .optional()
      .default(5)
      .openapi({
        description: 'Maximum number of relevant context snippets to retrieve',
        example: 5,
      }),
    scoreThreshold: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .default(0.65)
      .openapi({
        description: 'Minimum Cosine Similarity threshold (default: 0.65) to eliminate irrelevant noise',
        example: 0.65,
      }),
    embeddingProvider: z
      .enum(['workers-ai', 'openai'])
      .optional()
      .default('workers-ai')
      .openapi({
        description: 'Embedding model engine used to vectorise the incoming search query',
        example: 'workers-ai',
      }),
  })
  .openapi('QueryKnowledgeBaseRequest');

export type QueryKnowledgeBaseRequest = z.infer<typeof QueryKnowledgeBaseRequestSchema>;

export const QueryKnowledgeBaseResponseSchema = z
  .object({
    query: z.string().openapi({ example: 'How does Cloudflare Vectorize achieve low latency?' }),
    totalFound: z.number().int().openapi({ example: 3 }),
    citations: z.array(RagCitationSchema).openapi({ description: 'Filtered and ranked citations meeting the score threshold' }),
    augmentedContext: z.string().openapi({
      description: 'Pre-formatted contextual block ready for immediate injection into the System Prompt',
      example: '=== RELEVANT KNOWLEDGE CONTEXT ===\n[Source 1: Architecture_Guide.pdf (Score: 0.88)]\n...',
    }),
    latencyMs: z.number().optional().openapi({ example: 7 }),
  })
  .openapi('QueryKnowledgeBaseResponse');

export type QueryKnowledgeBaseResponse = z.infer<typeof QueryKnowledgeBaseResponseSchema>;
